import { Button } from '@mui/material';
import ReportProblemIcon from 'src/assets/images/icons/danger-light.svg';
import CheckCircleIcon from 'src/assets/images/icons/tick-circle-light.svg';
import DescriptionIcon from 'src/assets/images/icons/receipt-text.svg';
import DeviationIcon from 'src/assets/images/icons/danger-red.svg';
import ApprovedIcon from 'src/assets/images/icons/tick-circle-primary.svg';
import UnresolvedIcon from 'src/assets/images/icons/warning-circle-grey.svg';
import { useMaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { useMemo } from 'react';

import Modal from 'src/presentations/components/molecules/modal';
import { CustomTable } from 'src/presentations/components/molecules/table/Table';
import { useInvoiceSummaryFromParams } from 'src/use-cases/invoice/useInvoices';
import { formatNumber, beautifyEnum } from 'src/utils/utility';
import { useShipmentSelector } from '../ShipmentSelector';

interface ChargeDetailsModalProps {
  open: boolean;
  onClose: () => void;
}

const ChargeDetailsModal: React.FC<ChargeDetailsModalProps> = ({ open, onClose }) => {
  const { shipment } = useShipmentSelector();
  const { data: invoiceSummary } = useInvoiceSummaryFromParams();

  // Check if this is a No Rate Card Found (NRCF) shipment
  const isNRCF = shipment?.currentDeviationWorkflow === 'PROCUREMENT_NO_CONTRACT_REVIEW';

  const totalCharges = shipment?.invoiceCharges?.length ?? 0;
  const withDeviations = shipment?.deviations?.length ?? 0;
  const approvedByFreya = isNRCF ? 0 : totalCharges > 0 ? totalCharges - withDeviations : 0;

  const subtitle = (
    <div className='flex items-center gap-4 text-14 font-medium text-neutral-60 pt-4'>
      <span>Invoice No.</span>
      <span className='text-primary font-bold'>{invoiceSummary?.invoiceNumber || '-'}</span>
      <span className='mx-4'>|</span>
      <span className='text-primary font-bold'>{shipment?.shipmentDetail?.shipmentNumber || '-'}</span>
    </div>
  );

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'chargeName',
        header: 'Invoice Charge Name',
        enableEditing: false,
        size: 200,
        Cell: ({ row }) => <span className='text-14 text-black'>{row.original.chargeName || '-'}</span>,
      },
      {
        accessorKey: 'chargeDomain',
        header: 'Charge Category',
        enableEditing: false,
        size: 150,
        Cell: ({ row }) => (
          <span className='text-14 text-black'>{row.original.chargeDomain ? beautifyEnum(row.original.chargeDomain).toUpperCase() : '-'}</span>
        ),
      },
      {
        accessorKey: 'legType',
        header: 'Leg Type',
        enableEditing: false,
        size: 100,
        Cell: ({ row }) => {
          const legType = row.original.legType;
          if (!legType) return <span className='text-14 text-black'>-</span>;

          // Handle numeric leg types (1, 2, 3)
          if (typeof legType === 'number' || !isNaN(Number(legType))) {
            return <span className='text-14 text-black'>{legType}</span>;
          }

          // Handle string leg types (LEG_ONE, LEG_TWO, etc.)
          return <span className='text-14 text-black'>{beautifyEnum(String(legType)).toUpperCase()}</span>;
        },
      },
      {
        accessorKey: 'totalAmount',
        header: 'Charge Amount',
        enableEditing: false,
        size: 150,
        Cell: ({ row }) => (
          <span className='text-14 text-black'>
            {row.original.amountBeforeTaxInOriginalCurrency && row.original.originalCurrency
              ? `${row.original.originalCurrency} ${formatNumber(row.original.amountBeforeTaxInOriginalCurrency)}`
              : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        enableEditing: false,
        size: 100,
        Cell: ({ row }) => {
          // For NRCF shipments, show "NRCF" text
          if (isNRCF) {
            return (
              <div className='flex'>
                <span className='text-14'>NRCF</span>
              </div>
            );
          }

          // Use the new status field from invoiceCharges API response
          const chargeStatus = (row.original as any).status;

          // Status mapping:
          // "Resolved - Accepted" -> Green tick
          // "Resolved - Rejected" -> Red triangle
          // "Unresolved" -> Grey circle

          if (chargeStatus === 'Resolved - Accepted') {
            return (
              <div className='flex items-center justify-center'>
                <img src={ApprovedIcon} alt='resolved-accepted' className='w-20 h-20' />
              </div>
            );
          }

          if (chargeStatus === 'Resolved - Rejected') {
            return (
              <div className='flex items-center justify-center'>
                <img src={DeviationIcon} alt='resolved-rejected' className='w-20 h-20' />
              </div>
            );
          }

          if (chargeStatus === 'Unresolved') {
            return (
              <div className='flex items-center justify-center'>
                <img src={UnresolvedIcon} alt='unresolved' className='w-20 h-20' />
              </div>
            );
          }

          // Default fallback
          return (
            <div className='flex items-center justify-center'>
              <span className='text-14 text-black'>{chargeStatus || '-'}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'statusReason',
        header: 'Reason',
        enableEditing: false,
        size: 200,
        Cell: ({ row }) => {
          // Use statusReason field from the API response
          const statusReason = (row.original as any).statusReason;
          return <span className='text-14 text-black'>{statusReason || '-'}</span>;
        },
      },
    ],
    [shipment?.deviations, isNRCF]
  );

  const table = useMaterialReactTable({
    data: shipment?.invoiceCharges || [],
    enableRowNumbers: false,
    enableSorting: false,
    enablePinning: false,
    enableRowDragging: false,
    enableColumnActions: false,
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableEditing: false,
    editDisplayMode: 'table',
    columns,
    renderEmptyRowsFallback: () => <div className='px-16 py-24 text-center text-14 text-neutral-60'>No invoice charges found</div>,
    muiTableBodyCellProps: ({ cell }) => {
      const isLastColumn = cell.column.id === 'statusReason';
      return {
        sx: {
          borderBottom: '1px solid #dfdfdf !important',
          ...(isLastColumn && {
            borderRight: '0 !important',
          }),
        },
      };
    },
    muiTableBodyRowProps: ({ row, table }) => {
      const isLastRow = row.index === table.getRowModel().rows.length - 1;
      return {
        sx: {
          ...(isLastRow && {
            '& td': {
              borderBottom: 'none !important',
            },
          }),
        },
      };
    },
  });

  return (
    <Modal open={open} onClose={onClose} title='Charge Details' subtitle={subtitle} fullWidth maxWidth='md' PaperProps={{ sx: { borderRadius: '15px' } }}>
      <div className='-mx-24 -mb-20'>
        <div className='pt-24 px-24 pb-24'>
          {/* Summary Cards */}
          <div className={`grid ${isNRCF ? 'grid-cols-1' : 'grid-cols-3'} gap-16 mb-24`}>
            {/* Total No. of Charges */}
            <div className='bg-white border border-neutral-20 rounded-10 p-16 h-full flex flex-col justify-between min-h-[104px]'>
              <div className='flex items-center gap-12'>
                <div className='w-32 h-32 bg-neutral-10 rounded-6 flex items-center justify-center'>
                  <img src={DescriptionIcon} alt='receipt' className='w-20 h-20' />
                </div>
                <span className='text-14 xl:text-16 font-bold text-black '>Total No. of Charges</span>
              </div>
              <div className='text-16 xl:text-18 font-bold text-black mt-12 '>{totalCharges}</div>
            </div>

            {/* With Deviations - Hide for NRCF */}
            {!isNRCF && (
              <div className='bg-white border border-neutral-20 rounded-10 p-16 h-full flex flex-col justify-between min-h-[104px]'>
                <div className='flex items-center gap-12'>
                  <div className='w-32 h-32 bg-red-10 rounded-6 flex items-center justify-center'>
                    <img src={ReportProblemIcon} alt='danger' className='w-20 h-20' />
                  </div>
                  <span className='text-14 xl:text-16 font-bold text-black'>With Deviations</span>
                </div>
                <div className='text-16 xl:text-18 font-bold text-red-40 mt-12'>{withDeviations}</div>
              </div>
            )}

            {/* Approved by Freya - Hide for NRCF */}
            {!isNRCF && (
              <div className='bg-white border border-neutral-20 rounded-10 p-16 h-full flex flex-col justify-between min-h-[104px]'>
                <div className='flex items-center gap-12'>
                  <div className='w-32 h-32 bg-green-10 rounded-6 flex items-center justify-center'>
                    <img src={CheckCircleIcon} alt='approved' className='w-20 h-20' />
                  </div>
                  <span className='text-14 xl:text-16 font-bold text-black'>Approved by Freya</span>
                </div>
                <div className='text-16 xl:text-18 font-bold text-primary mt-12'>{approvedByFreya}</div>
              </div>
            )}
          </div>

          {/* Table */}
          <div>
            <CustomTable table={table} />
          </div>
        </div>

        {/* Footer / Close Button */}
        <div className='flex justify-end pt-16 pb-16 px-24 border-t border-neutral-20'>
          <Button
            variant='outlined'
            onClick={onClose}
            className='!border-neutral-30 !text-neutral-80 !font-bold !capitalize !px-24 !h-40 hover:!bg-neutral-10 hover:!border-neutral-40'
          >
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ChargeDetailsModal;
